import React from 'react';
import * as Yup from 'yup';
import { makeStyles } from '@material-ui/core/styles';
import { Formik } from 'formik';
import axios from '@lib/axios';
import { app } from '@root/config';
import { format } from 'date-fns';
import { toast } from "react-hot-toast";

import {
  Box,
  Grid,
  Button,
  TextField,
  Modal,
  Card,
  CardHeader,
  CardContent,
  InputAdornment,
  MenuItem, FormControlLabel, Switch
} from '@material-ui/core';
import { Alert, AlertTitle } from '@material-ui/lab';

import { useTranslation } from 'react-i18next';


const useStyles = makeStyles((theme) => ({
  modal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  paper: {
    position: 'absolute',
    backgroundColor: theme.palette.background.paper,
    border: '2px solid #000',
    boxShadow: theme.shadows[5]
  },
}));

const AccountModalForm = ({entity, open, merchantId, onClose, onUpdate, ...props}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const [isOpen, setIsOpen] = React.useState(open);
  const [isSubmitted, setIsSubmitted] = React.useState(false);
  const [isError, setIsError] = React.useState(false);
  const [banks, setBanks] = React.useState([]);

  React.useEffect(() => {
    setIsOpen(open);
  }, [open]);

  React.useEffect(() => {
    setIsOpen(open);
    if (open) {
      axios.get(`${app.api}/banks`, {
      params: {
        page: 0,
        count: 100,
      }
    }).then((response) => {
        setBanks(response.data.data);
      });
    }
  }, [open]);

  const handleOpen = () => {
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsSubmitted(false);
    setIsOpen(false);
    setIsError(false);
    if (onClose) {
        onClose();
    }
  };

  const values = entity ?
  {
    id: entity.id,
    name: entity.name,
    bank: entity.bank,
    overdraftLimit: entity.overdraftLimit / 100,
    isOverdraftUnlimited: entity.isOverdraftUnlimited,
    showInTerminalList: entity.showInTerminalList,
    showInAvailableFunds: entity.showInAvailableFunds,
  }
  :
  {
    id: null,
    fee: "",
    bank: null,
    overdraftLimit: 0,
    isOverdraftUnlimited: false,
    showInTerminalList: false,
    showInAvailableFunds: false,
  };

  const handleSubmit = async (values, { setErrors, setStatus, setSubmitting }) => {
    setIsError(false);

    await axios({
      method: values.id ? 'put' : 'post',
      url:  `${app.api}/merchant/${merchantId}/account` + (values.id ? `/${values.id}` : ''),
      data: {
        name: values.name,
        overdraftLimit: Math.floor(values.overdraftLimit * 100),
        isOverdraftUnlimited: values.isOverdraftUnlimited,
        bank: values.bank,
        showInTerminalList: values.showInTerminalList,
        showInAvailableFunds: values.showInAvailableFunds,
      }
    })
    .then(async (response) => {
      toast.success(values.id ? t("Successfully updated") : t("Successfully created"));
      handleClose();
      if (onUpdate) {
        onUpdate();
      }
    })
    .catch(async (error) => {
      setIsError(true);
    });
  };

  const validationSchema = 
    Yup.object().shape({
      name: Yup.string().min(5).max(255),
      overdraftLimit: Yup.number().min(0, 'Should be 0 or greater').required('Required'),
    });

  const form = (
    <>
    {isError &&
      <Alert severity='error'>
        <AlertTitle>{t('Error!')}</AlertTitle>
        {t('An error occurred during creating account. Please try again later.')}
      </Alert>
    }
    <Formik
      initialValues={values}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({
        errors,
        handleBlur,
        handleChange,
        handleSubmit,
        setFieldValue,
        isSubmitting,
        touched,
        values,}) => (
          <form noValidate onSubmit={handleSubmit} {...props}>
            <TextField 
              label={t('Name')}
              value={values.name}
              name="name"
              fullWidth
              onBlur={handleBlur}
              onChange={handleChange}
              margin="normal"
              error={Boolean(errors.name)}
            />
            <TextField
              error={Boolean(touched.bank && errors.bank)}
              fullWidth
              helperText={touched.bank && errors.bank}
              label={t("bank")}
              name="bank"
              onChange={handleChange}
              onBlur={handleBlur}
              select
              value={values.bank}
              margin="normal"
            >
              <MenuItem value={""}>
                {t("Select value")}
              </MenuItem>
              {banks.map((value) => (
                <MenuItem key={value.id} value={value.id}>
                  {value.name}
                </MenuItem>
              ))}
            </TextField>
            <Grid item xs={12}>
              <FormControlLabel control={
                <Switch
                  checked={values.isOverdraftUnlimited}
                  name='isOverdraftUnlimited'
                  onChange={handleChange}
                  color={values.isOverdraftUnlimited ? "success" : "error"}
                />} label={t('isOverdraftUnlimited')}
              />
            </Grid>

            <TextField
              label={t('overdraftLimit')}
              value={values.overdraftLimit}
              name="overdraftLimit"
              fullWidth
              onBlur={handleBlur}
              onChange={handleChange}
              margin="normal"
              type="number"
              error={Boolean(errors.overdraftLimit)}
              helperText={errors.overdraftLimit}
              InputProps={{
                endAdornment: <InputAdornment position="end">&#8376;</InputAdornment>,
              }}
            />

            <Grid item xs={12}>
              <FormControlLabel control={
                <Switch
                  checked={values.showInTerminalList}
                  name='showInTerminalList'
                  onChange={handleChange}
                  color={values.showInTerminalList ? "success" : "error"}
                />} label={values.showInTerminalList ? t('show in terminal balance list') : t('hide in terminal balance list')}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel control={
                <Switch
                  checked={values.showInAvailableFunds}
                  name='showInAvailableFunds'
                  onChange={handleChange}
                  color={values.showInAvailableFunds ? "success" : "error"}
                />} label={values.showInAvailableFunds ? t('use balance in available funds calculation') : t('not use balance in available funds calculation')}
              />
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ mt: 2 }}>
                <Button
                  color="primary"
                  disabled={isSubmitting || !Boolean(Object.keys(errors).length === 0)}
                  type="submit"
                  variant="contained"
                  size="small"
                >
                  {values.id ? t('Update') : t('Create')}
                </Button>
              </Box>
            </Grid>
          </form>
        )}
    </Formik>
    </>
  );

  return (
    <>
      <Modal
        open={isOpen}
        onClose={handleClose}
        aria-labelledby="simple-modal-title"
        aria-describedby="simple-modal-description"
        className={classes.modal}
      >
        <Card className={classes.paper}>
          <CardHeader title={values.id ? t('Edit account') : t('Create account')}/>
          <CardContent>
            {form}
          </CardContent>
        </Card>
      </Modal>
    </>
  );
}

export default AccountModalForm;
